import { Skeleton, Typography } from '@mui/material';
import { CircularLogo, Flex } from 'components';
import { DeeperViewTitle } from 'components/ComponentWithAnimatedBackArrow';
import { ExpiresLabel } from 'components/ExpiresLabel/ExpiresLabel';
import { LastQuoteUpdateDisplay } from 'components/LastQuoteUpdateDisplay/LastQuoteUpdateDisplay';
import { AddToListIcon } from 'components/SecurityChartSection/AddToListIcon';
import { SecurityValues } from 'components/SecurityValues/SecurityValues';
import { useShowAdditionalStats } from 'components/SecurityValues/SecurityValuesViewModel';
import { useAppWindowSizeVariable } from 'hooks/UseWindowSize';
import { T } from 'phoenix/assets/lang/T';
import { LiveDataNamespaces } from 'phoenix/constants/LiveDataNamespaces';
import { useText } from 'phoenix/hooks/UseText';
import { useAssetClassMetaV2 } from 'phoenix/models/AssetClasses/useAssetClass';
import { GetClientAccountsAction, GetSecurityQuoteAction } from 'phoenix/redux/actions';
import { GlobalState } from 'phoenix/redux/GlobalState';
import { OptionsPutCallAbbreviation } from 'phoenix/redux/models';
import { FuturesSymbol } from 'phoenix/redux/models/Futures/FuturesSymbol';
import { OptionSymbol } from 'phoenix/redux/models/Options/OptionSymbol';
import { useSecurityMetadataV2 } from 'phoenix/stores/SecurityMetadataV2Store';
import { ChangeColor, FormatDaysFromNow, FormatNumber } from 'phoenix/util';
import { QualifiedId } from 'phoenix/util/QualifiedId';
import { XS } from 'phoenix/xstream/XS';
import { useXstreamDispatch } from 'phoenix/xstream/XstreamProvider';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSecurityChartStore } from 'store/SecurityChart';
import { Routes } from 'util/Routes';
import style from './style.module.scss';

export const OptionContractHeaderSection = ({ symbol, onAddToListClick }: { symbol: string; onAddToListClick: () => void }): ReactElement => {
    const xdispatch = useXstreamDispatch();
    const dispatch = useDispatch();
    const [subscription, setSubscription] = useState<string>('');
    const [optSubscription, setOptSubscription] = useState<string>('');
    const text = useText((s) => s.optionContractScreen);
    const optSym = new OptionSymbol(symbol);
    const isFuture = FuturesSymbol.IsFuturesSymbol(symbol);
    const underlyingSymbol = (isFuture ? optSym.underlyingSymbolWithPrefix : optSym?.underlyingSymbol) || '';
    const latestUnderlyingQuote = XS.Quotes.use(underlyingSymbol);
    const underlying = (isFuture ? optSym.underlyingSymbolWithPrefix : optSym.underlyingSymbol) || '';
    const symbolName = useText((t) => t.general.symbolName(symbol));
    const { changeValue, changePercentValue } = useSecurityChartStore((s) => ({ changeValue: s.changeValue, changePercentValue: s.changePercentValue }));
    const accentColor = ChangeColor(changePercentValue || changeValue);
    const latestPrice = latestUnderlyingQuote?.price;
    const meta = useSecurityMetadataV2().getMetadataBySymbol(symbol);
    const { strikePrice = optSym.strike } = meta?.option || {};
    const metaUnderlying = useSecurityMetadataV2().getMetadataBySymbol(underlyingSymbol);
    const { formatPrice, getPriceFormatInfo } = useAssetClassMetaV2(symbol);
    const { decimalPlaces } = getPriceFormatInfo(meta);
    const { formatPrice: formatPriceUnderlying } = useAssetClassMetaV2(underlyingSymbol);
    const formatValue = (value: number) => formatPrice(value, meta);
    const formatUnderlying = (value?: number) => formatPriceUnderlying(value, metaUnderlying);
    const showAdditionalStats = useShowAdditionalStats();

    const titleSectionSizes = useAppWindowSizeVariable({
        tablet: { logo: 30, title: 24 },
        sm: { logo: 30, title: 24 },
        def: { logo: 55, title: 32 }
    });

    const valueWrapperMargin = useAppWindowSizeVariable({
        tablet: { paddingTop: 10, paddingLeft: 40 },
        sm: { paddingTop: 10, paddingLeft: 40 },
        def: { paddingTop: 0, paddingLeft: 15 }
    });

    const hideQuoteBadgeWhileScrubbing = useAppWindowSizeVariable({
        sm: true,
        md: true,
        lg: false,
        tablet: true
    });

    useEffect(() => {
        dispatch(GetClientAccountsAction());
    }, [dispatch]);

    const hasFuturesAccount = !!useSelector((s: GlobalState) => s.accounts.all)?.data?.find((a) => FuturesSymbol.IsFuturesSymbol(a.accountNumber));

    const subscribe = useCallback(async () => {
        if (!subscription) {
            const sub: string = underlyingSymbol ? await xdispatch(XS.Quotes.start(underlyingSymbol, LiveDataNamespaces.OptionContractScreen)) : null;
            if (sub) setSubscription(sub);
        }

        if (!optSubscription) {
            const sub: string = underlyingSymbol ? await xdispatch(XS.OptionQuotes.start([symbol], LiveDataNamespaces.OptionContractScreen)) : null;
            if (sub) setOptSubscription(sub);
        }
    }, [optSubscription, subscription, symbol, underlyingSymbol, xdispatch]);

    const unsubscribe = useCallback(() => {
        if (subscription) {
            XS.stop(subscription);
            XS.stop(optSubscription);
            setSubscription('');
            setOptSubscription('');
        }
    }, [optSubscription, subscription]);

    useEffect(() => {
        dispatch(GetSecurityQuoteAction(underlyingSymbol));
        if (!subscription) subscribe();
        return unsubscribe;
    }, [symbol, hasFuturesAccount, dispatch, underlyingSymbol, subscription, subscribe, unsubscribe]);

    const hideQuoteBadge = hideQuoteBadgeWhileScrubbing && showAdditionalStats;

    return (
        <Flex column align='flex-start' style={{ '--accentColor': accentColor } as React.CSSProperties}>
            <Flex>
                <DeeperViewTitle route={Routes.optionChain(underlying)} withBackArrow={!meta?.option?.isAdjusted}>
                    <CircularLogo altText={symbolName} size={titleSectionSizes?.logo} symbol={underlying} />
                    <Typography style={{ fontSize: titleSectionSizes?.title, marginLeft: 15 }} variant='h3'>
                        {QualifiedId.Format(optSym.underlyingSymbol)} {FormatNumber.toMoneyOpt2(strikePrice, { decimalPlaces, hideCurrencySymbol: isFuture })}{' '}
                        {T((t) => t.general.options.putCallWord(optSym?.putCall as OptionsPutCallAbbreviation))}
                    </Typography>
                    <Flex row justify='space-between' align='flex-start' style={{ gap: 16 }}>
                        <Flex column style={{ marginLeft: 10, paddingLeft: 10, borderLeft: 'solid 1px #e3e3e3' }}>
                            <Typography variant='h6'>{text.expiresOnDate(optSym.expDate)}</Typography>
                            <Typography variant='body1'>{FormatDaysFromNow(optSym.expDate)}</Typography>
                        </Flex>
                        <ExpiresLabel expiryDate={optSym.expDate} style={{ fontSize: 15 }} />
                    </Flex>
                </DeeperViewTitle>
                <span style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
                    <AddToListIcon securityId={symbol} onClick={onAddToListClick} />
                </span>
            </Flex>
            <Flex align='center' justify='space-between' style={{ boxSizing: 'border-box', width: '100%', ...valueWrapperMargin }}>
                <Flex column align='flex-start' justify='center'>
                    <SecurityValues symbol={symbol} formatValue={formatValue} />
                </Flex>
                {!hideQuoteBadge && (
                    <QuoteBadge
                        label={text.symbolLastQuote(QualifiedId.Format(optSym?.underlyingSymbol))}
                        loading={latestUnderlyingQuote?.loading || false}
                        value={formatUnderlying(latestPrice)}
                    />
                )}
            </Flex>
            <LastQuoteUpdateDisplay debounceMs={50 * 1000} prefix='Price as of' qsi={symbol} style={{ marginTop: 10, color: 'rgba(150,150,150,0.8)', fontSize: 11 }} />
        </Flex>
    );
};

export const QuoteBadge = ({ label, loading, value }: { label: string; loading: boolean; value: string }): ReactElement =>
    loading ? (
        <Skeleton animation='wave' width={100} />
    ) : (
        <Flex align='center' className={style.streamingVolumeWrapper} justify='flex-end'>
            <Typography style={{ marginRight: 7, fontWeight: 500 }} variant='h6'>
                {label}
            </Typography>
            <Typography variant='h6'>{value}</Typography>
        </Flex>
    );
